import WaveSurfer from 'wavesurfer.js';
import RegionsPlugin from 'wavesurfer.js/dist/plugins/regions.esm.js';
import WebAudioPlayer from 'wavesurfer.js/dist/webaudio.js';
import { SoundTouchNode } from '@soundtouchjs/audio-worklet';
import { onScopeDispose } from 'vue';
import { useGlobalAudioContext } from './useGlobalAudioContext';

const SOUNDTOUCH_PROCESSOR_URL = '/soundtouch-processor.js';

// SoundTouchJS auto-sequence formula from @soundtouchjs/core (Stretch.js):
// sequence window length in ms = AUTOSEQ_C + AUTOSEQ_K × tempo, linear from
// 125ms at tempo 0.25 to 50ms at tempo 4.0. The window length approximates
// the WSOLA buffer-fill latency. Tune AUTOSEQ_C up if audio still trails the
// metronome, down if audio now leads it.
const AUTOSEQ_C = 130;
const AUTOSEQ_K = -20;

const globalAudio = useGlobalAudioContext();
type WaveSurferInitOptions = {
  container: HTMLElement;
  height: number;
  waveColor: string;
  progressColor: string;
};

// The one thing that belongs to the AudioContext rather than to a player:
// `register` is a bare `addModule`, which re-runs the worklet script and throws
// on a processor name the context already has. Every player on a context shares
// this registration, and it outlives all of them — never reset it on teardown.
let workletRegisteredFor: AudioContext | null = null;

const registerSoundTouchWorklet = async (context: AudioContext) => {
  if (workletRegisteredFor === context) return;
  await SoundTouchNode.register(context, SOUNDTOUCH_PROCESSOR_URL);
  workletRegisteredFor = context;
};

// One player per caller, i.e. per AudioWaveform. Two waveforms are alive at once
// whenever a cached view holds one — a parked piece editor and the game view,
// say — and on a shared player the second `load()` aborts the first's fetch and
// re-points the rendered container at the newcomer. The player is destroyed with
// the scope that asked for it.
export function useWaveSurfer() {
  let wavesurferInstance: WaveSurfer | null = null;
  let regionsPluginInstance: RegionsPlugin | null = null;
  let currentAudioUrl: string | undefined;
  let soundTouchNode: SoundTouchNode | null = null;

  // Audio graph: gain → [SoundTouch when rate ≠ 1] → destination. SoundTouch
  // mirrors WaveSurfer's source rate to cancel the pitch shift it causes.
  // Bypassed at rate === 1 to skip ~70ms of WSOLA window latency.
  const routeGainNode = (gainNode: GainNode, rate: number) => {
    if (!soundTouchNode) return;
    gainNode.disconnect();
    gainNode.connect(rate === 1 ? gainNode.context.destination : soundTouchNode);
  };

  // A node can't outlive its context, and it has no flush API — both callers
  // below need a replacement built and wired the same way.
  const replaceSoundTouchNode = (context: AudioContext): SoundTouchNode => {
    soundTouchNode?.disconnect();
    soundTouchNode = new SoundTouchNode(context);
    soundTouchNode.connect(context.destination);
    return soundTouchNode;
  };

  const ensureSoundTouchWiring = async (context: AudioContext, gainNode: GainNode, rate: number) => {
    await registerSoundTouchWorklet(context);
    // The registration flag is shared between players now, so the node's own
    // context is what marks it stale.
    const node = soundTouchNode?.context === context ? soundTouchNode : replaceSoundTouchNode(context);
    node.playbackRate.value = rate;
    routeGainNode(gainNode, rate);
  };

  globalAudio.onAudioContextRecreated((context) => {
    if (!wavesurferInstance) return;
    const currentVolume = wavesurferInstance.getVolume();
    const currentPlaybackRate = wavesurferInstance.getPlaybackRate();

    // Keep the same WaveSurfer instance; only swap the WebAudio backend media element.
    // @ts-ignore WebAudioPlayer is a supported media backend.
    const media = new WebAudioPlayer(context);
    media.volume = currentVolume;
    media.playbackRate = currentPlaybackRate;
    if (currentAudioUrl) {
      media.src = currentAudioUrl;
    }
    // @ts-ignore WaveSurfer accepts WebAudioPlayer as media element.
    wavesurferInstance.setMediaElement(media);
    ensureSoundTouchWiring(context, media.getGainNode(), currentPlaybackRate);
  });

  const createWaveSurferInstance = (options: WaveSurferInitOptions): WaveSurfer => {
    regionsPluginInstance = RegionsPlugin.create();
    const player = new WebAudioPlayer(globalAudio.getAudioContext());
    const instance = WaveSurfer.create({
      // ts-ignore - WebAudioPlayer is not included in the official types, but it is a valid backend option in wavesurfer.js
      // @ts-ignore
      media: player,
      container: options.container,
      blobMimeType: 'audio/mpeg',
      waveColor: options.waveColor,
      progressColor: options.progressColor,
      cursorColor: 'transparent',
      barWidth: 2,
      barGap: 1,
      height: options.height,
      normalize: true,
      interact: false,
      autoCenter: false,
      autoScroll: false,
      minPxPerSec: 0,
      backend: 'WebAudio',
      plugins: [regionsPluginInstance],
    });
    ensureSoundTouchWiring(globalAudio.getAudioContext(), player.getGainNode(), instance.getPlaybackRate());
    return instance;
  };

  const getWaveSurfer = (options?: WaveSurferInitOptions): WaveSurfer => {
    if (!wavesurferInstance) {
      if (!options) {
        throw new Error('WaveSurfer is not initialized. Call getWaveSurfer(options) first.');
      }
      wavesurferInstance = createWaveSurferInstance(options);
      return wavesurferInstance;
    }

    if (options) {
      wavesurferInstance.setOptions({
        container: options.container,
        height: options.height,
        waveColor: options.waveColor,
        progressColor: options.progressColor,
      });
    }

    return wavesurferInstance;
  };

  const getRegionsPlugin = (): RegionsPlugin => {
    if (!regionsPluginInstance) {
      throw new Error('Regions plugin is not initialized. Initialize WaveSurfer first.');
    }
    return regionsPluginInstance;
  };

  const loadAudioFile = async (audioFile: File) => {
    if (!audioFile.name) return;
    const wavesurfer = getWaveSurfer();
    if (currentAudioUrl) {
      URL.revokeObjectURL(currentAudioUrl);
    }
    const blob = new Blob([await audioFile.arrayBuffer()]);
    currentAudioUrl = URL.createObjectURL(blob);
    await wavesurfer.load(currentAudioUrl);
  };

  const setPlaybackRate = (rate: number) => {
    wavesurferInstance?.setPlaybackRate(rate);
    if (!wavesurferInstance || !soundTouchNode) return;
    soundTouchNode.playbackRate.value = rate;
    const player = wavesurferInstance.getMediaElement() as unknown as WebAudioPlayer;
    routeGainNode(player.getGainNode(), rate);
  };

  // Wall-clock latency the audio incurs because of SoundTouch's WSOLA buffer.
  // Zero when bypassed (rate === 1). Callers compensate by scheduling audio
  // earlier or holding the visual clock back for this many ms.
  const getProcessingLatencyMs = (): number => {
    if (!wavesurferInstance) return 0;
    const rate = wavesurferInstance.getPlaybackRate();
    if (rate === 1) return 0;
    return AUTOSEQ_C + AUTOSEQ_K * rate;
  };

  // SoundTouch's worklet has no flush API, but its output FIFO holds ~one
  // WSOLA window of already-processed samples — resuming after a seek emits
  // those (audio from the pre-pause position) before the new source's
  // samples. Discard the node and spin up a fresh one. No-op at rate 1
  // (worklet is bypassed, not in the audio path).
  const flushSoundTouchBuffer = () => {
    if (!wavesurferInstance) return;
    const rate = wavesurferInstance.getPlaybackRate();
    if (rate === 1) return;
    const context = globalAudio.getAudioContext();
    if (workletRegisteredFor !== context) return;
    replaceSoundTouchNode(context).playbackRate.value = rate;
    const player = wavesurferInstance.getMediaElement() as unknown as WebAudioPlayer;
    routeGainNode(player.getGainNode(), rate);
  };

  // Releases the decoded audio and this player's slice of the audio graph.
  // Aborts a load still in flight, which is why callers treat an `AbortError`
  // out of `loadAudioFile` as teardown rather than as a failure.
  onScopeDispose(() => {
    wavesurferInstance?.destroy();
    wavesurferInstance = null;
    regionsPluginInstance = null;
    soundTouchNode?.disconnect();
    soundTouchNode = null;
    if (currentAudioUrl) {
      URL.revokeObjectURL(currentAudioUrl);
      currentAudioUrl = undefined;
    }
  });

  return {
    getWaveSurfer,
    getRegionsPlugin,
    loadAudioFile,
    setPlaybackRate,
    getProcessingLatencyMs,
    flushSoundTouchBuffer,
  };
}
